import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { bindActionCreators, Dispatch } from 'redux';
import { Location, History } from 'history';
import { selectClinic } from 'actions/clinicManager';
import { routes } from 'utils/routeHelper';
import { scopes, hasRequiredScope } from 'utils/scope';
import Program from 'utils/program';
import Tabbar from 'components/common/ClinicManager/Tabbar';
import ManageLocations from 'components/screens/ManageLocations';
import ManageClinicians from 'components/screens/ManageCliniciansKakariko';
import { ManageLocations as ManageLocationsNew } from 'components/screens/ManageLocationsKakariko';
import { ManagePatients as ManagePatientsNew } from 'components/screens/ManagePatientsKakariko';
import { ProgramDTO } from '@peartherapeutics/weaver-api-client';
import { FeatureFlag } from '../../library/FeatureFlag';
import { KAKARIKO_SCREENS } from '../../../feature-flags';
import './styles.scss';

interface IOwnProps {
  location: Location;
  sortProp: Nullable<string>;
  sortOrder: Nullable<string>;
  sortBy: (prop: string) => void;
  history: History;
}

interface IStateProps {
  clinicName: string;
  clinicId: string;
  currentUserRoles: string[];
  currentUserScopes: string[];
}

interface IDispatchProps {
  selectClinic: (clinicName: string, clinicId: string) => void;
}

type ClinicManagerType = IStateProps & IDispatchProps & IOwnProps;

const ClinicManager = (clinicManagerProps: ClinicManagerType) => {
  const [currentProgram, setProgram] = useState<ProgramDTO>(Program.get());
  const {
    currentUserRoles,
    currentUserScopes,
    location,
    clinicName,
    clinicId,
  } = clinicManagerProps;
  const hasScope = hasRequiredScope(currentUserScopes);

  const isAbleToAccessManagePatients = hasScope(scopes.MANAGE_PATIENTS_LIST);
  const isAbleToAccessManageClinicians = hasScope(
    scopes.MANAGE_CLINICIANS_LIST,
  );

  const isManageLocationsTabShown = hasScope(scopes.LOCATION_READ);

  return (
    <div styleName="container" data-testid="clinic-manager">
      <Tabbar
        isManagePatientsTabShown={isAbleToAccessManagePatients}
        isManageCliniciansTabShown={isAbleToAccessManageClinicians}
        isManageLocationsTabShown={isManageLocationsTabShown}
        location={location}
        selectClinic={clinicManagerProps.selectClinic}
        setProgram={setProgram}
      />
      <Switch>
        <Route
          path={routes.MANAGE_LOCATIONS}
          render={() => (
            <FeatureFlag
              featureFlag={KAKARIKO_SCREENS}
              featureElement={
                <ManageLocationsNew currentUserScopes={currentUserScopes} />
              }
            >
              <ManageLocations
                currentUserScopes={currentUserScopes}
                isLocationWrite={hasScope(scopes.LOCATION_WRITE)}
              />
            </FeatureFlag>
          )}
        />
        <Route
          path={routes.MANAGE_CLINICIANS}
          render={(routeProps) => (
            <ManageClinicians
              {...routeProps}
              clinicId={clinicId}
              clinicName={clinicName}
            />
          )}
        />
        <Route
          path={routes.MANAGE_PATIENTS}
          render={(routeProps) => (
            <ManagePatientsNew
              {...routeProps}
              clinicId={clinicId}
              clinicName={clinicName}
              currentUserRoles={currentUserRoles}
              currentUserScopes={currentUserScopes}
              currentProgram={currentProgram?.name}
            />
          )}
        />
      </Switch>
    </div>
  );
};

const mapStateToProps = (state: IRootStateType) => ({
  clinicName: state.clinicManager.clinicName,
  clinicId: state.clinicManager.clinicId,
  currentUserRoles: state.authenticate?.profile?.roles ?? [],
  currentUserScopes: state?.authenticate?.profile?.scopes ?? [],
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators({ selectClinic }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ClinicManager);
